import { EmptyState, FlexColumn, Loading, MaterialIcon, useLoading, useModal } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FLEET_TRIGGER_TYPE_LABELS } from '../../../constants/fleet';
import { TABLE_COLUMNS_NAME } from '../../../constants/fleetMaintenanceRules';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { apiParamsState } from '../../../storage/app';
import { FleetMaintenanceRule, FleetTriggerType } from '../../../types/fleet';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import { StyledTooltip } from '../../ui/StyledTooltip';
import TableEmptyActionCell from '../../ui/TableEmptyActionCell';
import useFleetMaintenanceRules from '../hooks/useFleetMaintenanceRules';
import FleetMaintenanceRuleModal from './FleetMaintenanceRuleModal';

function FleetMaintenanceRuleList() {
  const { theme, css, classes } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [maintenanceRules, setMaintenanceRules] = useState<FleetMaintenanceRule[]>();
  const {
    page,
    onReload,
    onChangePage,
    loadFleetMaintenanceRules,
    reload,
    search,
    filters: { maintenanceTypeId, triggerType },
  } = useFleetMaintenanceRules();
  const [totalItems, setTotalItems] = useState(0);
  const { loading, stopLoading, startLoading } = useLoading(true);
  const { showFailFeedback } = useFeedback();
  const [selectedMaintenanceRule, setSelectedMaintenanceRule] = useState<FleetMaintenanceRule>();
  const { openModal, closeModal, showModal } = useModal();

  useUpdateEffect(() => {
    onChangePage(0);
  }, [search, reload, maintenanceTypeId, triggerType, apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (page === 0) {
      onChangePage(1);
      return;
    }
    const loadData = async () => {
      startLoading();
      await loadAssets();
      stopLoading();
    };

    loadData();
  }, [page]);

  const loadAssets = async () => {
    const { configurations, error, total } = await loadFleetMaintenanceRules();
    if (error) {
      showFailFeedback(error);
      setMaintenanceRules(undefined);
      setTotalItems(0);
    } else {
      setMaintenanceRules(configurations);
      setTotalItems(total ?? 0);
    }
  };

  const onOpenDetailModal = (maintenanceRule: FleetMaintenanceRule) => {
    setSelectedMaintenanceRule(maintenanceRule);
    openModal();
  };

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
      {!maintenanceRules && !loading && <EmptyState title="Todavía no cuentas con ninguna regla." />}
      {!maintenanceRules && loading && <Loading />}
      {maintenanceRules && (
        <>
          {!!maintenanceRules?.length && (
            <StyledPaginatedTable
              columns={TABLE_COLUMNS_NAME}
              data={maintenanceRules.map((maintenanceRule) => {
                const canEdit = !!maintenanceRule.client;
                const pluralLabel = !(maintenanceRule.trigger === 1);
                const triggerTypeLabel =
                  maintenanceRule.triggerType === FleetTriggerType.distance ? 'km' : pluralLabel ? 'días' : 'día';
                return {
                  items: [
                    maintenanceRule.name,
                    maintenanceRule.description,
                    FLEET_TRIGGER_TYPE_LABELS[maintenanceRule.triggerType] ?? '-',
                    maintenanceRule.trigger.toLocaleString() + ` ${triggerTypeLabel}`,
                    maintenanceRule.maintenanceType.value,
                    canEdit ? (
                      <StyledTooltip
                        key={`details-${maintenanceRule._id}`}
                        content="Editar"
                        showArrow={false}
                      >
                        <Button
                          size="mini"
                          shape="square"
                          onClick={() => onOpenDetailModal(maintenanceRule)}
                          kind="tertiary"
                        >
                          <MaterialIcon
                            name="edit"
                            size={theme.sizing.scale700}
                          />
                        </Button>
                      </StyledTooltip>
                    ) : (
                      <TableEmptyActionCell />
                    ),
                  ],
                };
              })}
              totalItems={totalItems}
              page={page}
              onPageChange={onChangePage}
              loading={loading}
            />
          )}
          {!maintenanceRules.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
      <FleetMaintenanceRuleModal
        isOpen={showModal}
        onClose={(update) => {
          if (update) onReload();
          closeModal();
        }}
        selectedMaintenanceRule={selectedMaintenanceRule}
      />
    </FlexColumn>
  );
}

export default FleetMaintenanceRuleList;
